import { createStore, applyMiddleware } from 'redux';
import sentryCapturing from './side-effects/sentryCapturing';
import thunkMiddleware from 'redux-thunk';
import { createLogger } from 'redux-logger';
import rootReducer from './reducers';
import RootState from './RootState';
import { isRunningInProduction, isPro, isElectronApp } from '../utils';
import tourUpdate from './side-effects/tourUpdate';
import didChangeFlyer from './side-effects/didChangeFlyer';
import didRemoveAsset from './side-effects/didRemoveAsset';
import startCloudRenderings from './side-effects/startCloudRenderings';
import motionSourcePlayback from './side-effects/motionSourcePlayback';
import Plan from '../buy/Plan';
import SaveState from './SaveState';
import { isSoundtrackSignal, SignalSource } from '../data/SignalSource';
import { Frame } from '../data/Frame';
import Layer from '../data/Layer';
import Alea from 'alea';
import SimplexNoise from 'simplex-noise';
import { updateWindowPlan } from './side-effects/updateWindowPlan';
import User from 'data/User';
import * as Actions from './actions';
import { undoMiddleware } from './side-effects/undo';
import { didAddAssetMiddleware } from './side-effects/didAddAsset';
import { testSavingMiddleware } from './side-effects/testSaving';
import { resumePlaybackMiddleware } from './side-effects/resumePlayback';
import { PlatformType } from 'data/PlatformType';
import { cacheFigmaLayers } from './side-effects/cacheFigmaLayers';
import firebase from 'firebase/app';
import 'firebase/database';
import { colorizeSprite } from 'utils/PixiUtils';

const PIXI = (window as any).PIXI;
// PIXI.settings.PREFER_ENV = PIXI.ENV.WEBGL;

const FilterCache = require('@beatflyer/shaders-lib').FilterCache;

let loggerMiddleware = createLogger();
let store = createStore(
  rootReducer,
  applyMiddleware(
    undoMiddleware,
    thunkMiddleware,
    loggerMiddleware,
    // smallerImage,
    // promptSplit,
    sentryCapturing,
    tourUpdate,
    didChangeFlyer,
    didRemoveAsset,
    didAddAssetMiddleware,
    startCloudRenderings,
    motionSourcePlayback,
    updateWindowPlan,
    testSavingMiddleware,
    resumePlaybackMiddleware,
    cacheFigmaLayers
  )
);

if (!isRunningInProduction()) {
  if ((window as any).Cypress) {
    (window as any).store = store;
  }

  if (module.hot) {
    module.hot.accept('./reducers', () => {
      store.replaceReducer(rootReducer);
    });
  }
}

export const getStoreState = () => store.getState() as RootState;
export const storeDispatch = (a: any) => store.dispatch(a);
export const subscribeToStoreState = (callback: () => void) =>
  store.subscribe(callback);

// if (isRunningInProduction() || (window as any).Cypress) {
window.onbeforeunload = (e: BeforeUnloadEvent) => {
  const { flyer, app } = getStoreState();
  if (app.isBeatflyerLite) {
    return;
  }
  if (
    // app.user &&
    //   canUserSave(app.user) &&
    flyer.saveStatus !== SaveState.Saved
  ) {
    if (isElectronApp()) {
      const { closeCurrentWindow, dialog } = (window as any).electron.remote;
      if (closeCurrentWindow !== undefined) {
        dialog
          .showMessageBox({
            type: 'question',
            buttons: ['Close', 'Cancel'],
            title: 'Lose unsaved work?',
            message:
              'You have unsaved work in the app and closing it will lose it. Do you still want to close the app?'
          })
          .then((choice: any) => {
            if (choice.response === 0) {
              closeCurrentWindow();
            }
          });
        e.returnValue = false;
      }
    } else {
      e.returnValue =
        'You have unsaved changes, are you sure you want to leave?';
    }
  }
};
// }

export const isUserPro = (): boolean => {
  const user = getStoreState().app.user;
  return isPro(user);
};

export const shouldShowWatermark = (): boolean => {
  const app = getStoreState().app;
  if (app.isBeatflyerLite) {
    return false;
  }
  if (app.isCloudRenderer) {
    return !app.hideRendererWatermark;
  }
  const user = app.user;
  if (user) {
    return !(isPro(user) && user.plan !== Plan.ProEvangelist);
  }
  return true;
};

export const filterCache = new FilterCache();

let renderer: PIXI.WebGLRenderer | null;
export const getRenderer = (): PIXI.WebGLRenderer => {
  if (!renderer) {
    const resolution = (store.getState() as RootState).flyer.resolution;
    renderer = new PIXI.Renderer({
      width: resolution.width,
      height: resolution.height,
      transparent: true
    });
    (renderer as any).context.gl.getExtension('OES_standard_derivatives');
    // if (!isRunningInProduction()) {
    //   renderer?.on('postrender', () => {
    //     console.log('--- PIXI Rendered');
    //   });
    // }
  }
  return renderer!;
};

let stage: PIXI.Container | null;
export const getStage = (): PIXI.Container | null => {
  return stage;
};
export const setStage = (newStage: PIXI.Container) => {
  stage = newStage;
};

let stageBackground: PIXI.Sprite | undefined;
export const getStageBackground = (): PIXI.Sprite => {
  if (stageBackground === undefined) {
    stageBackground = new PIXI.picture.Sprite();
    stageBackground!.texture = PIXI.Texture.WHITE;
    colorizeSprite(stageBackground!, '#000000');
  }
  return stageBackground!;
};

export const getCanvasScale = (): number => {
  const canvas = document.querySelector(
    '.artboard canvas'
  ) as HTMLCanvasElement;
  if (canvas) {
    const { resolution } = (store.getState() as RootState).flyer;
    return canvas.getBoundingClientRect().width / resolution.width;
  } else {
    return 1;
  }
};

const radians = 0.0174533;
const seed = new (Alea as any)(0);
const noise = new SimplexNoise(seed);
const smoothGlitch = (value: number, threshold: number): number => {
  if (value < threshold) {
    return 0;
  }
  value -= threshold;
  const space = 1 - threshold;
  return value / space;
};

export const getSignalAt = (signal: SignalSource, position: number): number => {
  const { motionSource, soundtrack } = getStoreState().flyer;
  // if (signal === SignalSource.Main && soundtrack === motionSource) {
  //   signal = SignalSource.BeatSmooth;
  // }
  if (isSoundtrackSignal(signal)) {
    const time = position * motionSource.getDuration();
    if (!soundtrack) {
      throw new Error(`Cannot find soundtrack when signal is ${signal}`);
    }
    return soundtrack.getLevelAt(time, signal);
  } else {
    switch (signal) {
      case SignalSource.Full:
        return 1;
      case SignalSource.Default:
        return motionSource.getLevelAt(position * motionSource.getDuration());
      case SignalSource.Playback:
        return position;
      case SignalSource.PlaybackLoop:
        return Math.sin(position * radians * 180);
      case SignalSource.Seconds15Loop:
        const time = position * motionSource.getDuration();
        const relativePosition = (time % 15000) / 15000;
        return Math.sin(relativePosition * radians * 180);
      case SignalSource.GlitchOne: {
        const val = noise.noise2D(
          0,
          position * motionSource.getDuration() * 0.001
        );
        return Math.min(1, smoothGlitch(val, 0.5) * 2);
      }
      case SignalSource.GlitchTwo: {
        const val = noise.noise2D(
          position * motionSource.getDuration() * 0.001,
          0
        );
        return Math.min(1, smoothGlitch(val, 0.5) * 2);
      }
      case SignalSource.RandomOne:
        return (
          0.5 +
          noise.noise2D(0, position * motionSource.getDuration() * 0.001) * 0.5
        );
      case SignalSource.RandomTwo:
        return (
          0.5 +
          noise.noise2D(position * motionSource.getDuration() * 0.001, 0) * 0.5
        );
      default:
        break;
    }
  }
  console.warn(`Could not find level for signal ${signal}`);
  return 0;
};

export const getNodeById = (id: string): Layer | Frame | null => {
  const { layers } = getStoreState().flyer;
  for (const layer of layers) {
    if (layer.id === id) {
      return layer;
    }
    for (const frame of layer.frames) {
      if (frame.id === id) {
        return frame;
      }
    }
  }
  console.error(`Cannot find frame with id ${id}, had ${layers.length} layers`);
  return null;
};

let isListeningToPlanChange = false;
export const listenToPlanChange = () => {
  if (isListeningToPlanChange) {
    return;
  }
  const { user } = getStoreState().app;
  if (!user) {
    alert(`Can't listen to Upgrade if there is no user`);
    return;
  }
  let currentPlan = user.plan;
  let currentPlanExporation = user.planExpiration;
  const ref = firebase.database().ref(`users/${user?.uid}`);
  ref.on('value', async (snap: any) => {
    const userData: User = snap.val();
    if (
      isPro(userData) &&
      (userData.plan !== currentPlan ||
        userData.planExpiration !== currentPlanExporation)
    ) {
      currentPlan = userData.plan;
      currentPlanExporation = userData.planExpiration;
      storeDispatch(Actions.updateUser(userData));
    }
  });
  isListeningToPlanChange = true;
};

if (getStoreState().app.platform === PlatformType.iOSApp) {
  (window as any).beatflyerAppWillEnterBackground = () => {
    storeDispatch(Actions.pageWillEnterBackground());
  };
  (window as any).beatflyerAppDidEnterForeground = () => {
    storeDispatch(Actions.pageDidEnterForeground());
  };
} else {
  window.addEventListener('blur', () => {
    storeDispatch(Actions.pageWillEnterBackground());
  });
  window.addEventListener('focus', () => {
    storeDispatch(Actions.pageDidEnterForeground());
  });
}



// WEBPACK FOOTER //
// ./src/state/Store.ts